import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ScrollText, CalendarDays, Clock } from "lucide-react";
import { EMPTY_CCNL } from "./adminUtils";
import { CcnlFormDialog } from "./CcnlFormDialog";
import { ConfirmDialog } from "./ConfirmDialog";

const numFields = ["annual_ferie_days", "annual_rol_hours", "exfestivita_hours", "weekly_hours", "monthly_salaries", "probation_days", "notice_days"];

export const CcnlTab = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ccnl").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome del CCNL"); return; }
    setSaving(true);
    try {
      const body = { ...form };
      numFields.forEach((f) => { body[f] = Number(body[f]) || 0; });
      if (form._edit) { delete body._edit; delete body.id; delete body.created_at; await api.put(`/ccnl/${form.id}`, body); toast.success("CCNL aggiornato"); }
      else { await api.post("/ccnl", body); toast.success("CCNL creato"); }
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/ccnl/${toDelete.id}`); toast.success("CCNL eliminato"); setToDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-[13px] text-gray-500 max-w-xl">Definisci i parametri dei contratti collettivi. Le ferie/ROL full-time vengono riproporzionate in base alla % part-time del dipendente nell'anagrafica.</p>
        <button onClick={() => setForm({ ...EMPTY_CCNL })} data-testid="add-ccnl-btn" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Plus size={16} /> Nuovo CCNL</button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]">Nessun CCNL definito. Aggiungi il primo contratto collettivo.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`ccnl-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <span className="w-11 h-11 rounded-lg bg-[#16345c]/10 text-[#16345c] flex items-center justify-center mb-3"><ScrollText size={22} /></span>
                <div className="flex gap-1">
                  <button onClick={() => setForm({ ...c, _edit: true })} data-testid={`edit-ccnl-${c.id}`} className="w-8 h-8 flex items-center justify-center rounded-sm text-[#16345c] hover:bg-[#16345c]/10 transition-colors"><Pencil size={15} /></button>
                  <button onClick={() => setToDelete(c)} data-testid={`delete-ccnl-${c.id}`} className="w-8 h-8 flex items-center justify-center rounded-sm text-[#c00510] hover:bg-red-50 transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="text-[16px] font-bold text-[#16345c] leading-tight">{c.name}</div>
              <div className="text-[12.5px] text-gray-500 mb-3">{c.sector || "—"}</div>
              <div className="flex flex-wrap gap-2 text-[12px] pt-3 border-t border-gray-100">
                <span className="flex items-center gap-1 text-gray-600 bg-gray-50 px-2 py-1 rounded-sm"><CalendarDays size={13} className="text-[#009640]" /> {c.annual_ferie_days} gg ferie</span>
                <span className="flex items-center gap-1 text-gray-600 bg-gray-50 px-2 py-1 rounded-sm"><Clock size={13} className="text-[#0d6cb8]" /> {c.annual_rol_hours} ore ROL</span>
                <span className="text-gray-500 bg-gray-50 px-2 py-1 rounded-sm">{c.weekly_hours}h/sett</span>
                <span className="text-gray-500 bg-gray-50 px-2 py-1 rounded-sm">{c.monthly_salaries} mens.</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <CcnlFormDialog form={form} setForm={setForm} saving={saving} onSave={save} onClose={() => setForm(null)} />

      <ConfirmDialog open={!!toDelete} onCancel={() => setToDelete(null)} onConfirm={remove} title="Eliminare il CCNL?">
        Stai per eliminare il contratto <b>{toDelete?.name}</b>. I dipendenti che lo avevano assegnato resteranno senza CCNL.
      </ConfirmDialog>
    </div>
  );
};
